/**
 * @file PageBreadcrumbsField.tsx
 * @description Поле «Хлебные крошки» в группе «Для поиска» «Настроек
 * страницы» (phase19.16, партия «SEO-техника», v0.19.65; SB146, SB126; макет
 * uiux-seo-tech.md §3.4): сегменты «Как на сайте · Показывать · Скрыть»,
 * под ними «На сайте: показываются (Весь сайт → Меню → Крошки ›)» —
 * переключатель сайта по черновику вида (то, что видит канва), и при своём
 * выборе — «У этой страницы: скрыты» (QA v0.19.65), и «Разметка для поиска
 * выводится, только когда крошки видны». Выбор — часть черновика страницы,
 * на сайт уходит при её публикации.
 *
 * На главной крошек нет никогда: поле недоступно с подписью, значение
 * сохраняется. Без права правки страницы — недоступно.
 */

import { readNavigationOptions, type SitePageBreadcrumbs } from "@chaiberi/shared";

import { Button } from "../../ui/button";
import { Segmented } from "../../site-view/Segmented";
import { useSiteView } from "../../site-view/site-view-context";
import { useBuilder } from "../builder-context";
import { useHasPermission } from "../../../auth/hooks";
import {
  BREADCRUMBS_HOME_NOTE,
  BREADCRUMBS_MARKUP_NOTE,
  PAGE_BREADCRUMBS_OPTIONS,
  breadcrumbsWord,
  pageOwnBreadcrumbsWord,
} from "../../../lib/site-builder/page-breadcrumbs";

interface Props {
  value: SitePageBreadcrumbs;
  onChange: (value: SitePageBreadcrumbs) => void;
  /** Главная страница — крошек нет никогда. */
  home: boolean;
  disabled: boolean;
}

export function PageBreadcrumbsField({ value, onChange, home, disabled }: Props) {
  const view = useSiteView();
  const b = useBuilder();
  // Вкладку «Крошки» правит право контента (часть MENUS) — без него ссылка ни к чему.
  const canContent = useHasPermission("SHOP_MANAGE_CONTENT");
  // Переключатель сайта — из черновика вида (несохранённая правка вкладки «Крошки» тоже видна).
  const siteOn = view.parts?.navigation?.navigation.breadcrumbs ?? readNavigationOptions(view.draft?.navigation).breadcrumbs;
  const off = disabled || home;
  const own = pageOwnBreadcrumbsWord(value);
  const options = PAGE_BREADCRUMBS_OPTIONS.map((o) => ({ ...o, disabled: off }));

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <span className="text-sm font-medium text-on-surface">Хлебные крошки</span>
      <Segmented aria-label="Хлебные крошки" options={options} value={value} onChange={onChange} fill />
      {home ? (
        <p className="type-caption text-on-surface-variant">{BREADCRUMBS_HOME_NOTE}</p>
      ) : (
        <p className="type-caption flex flex-wrap items-center gap-x-1 text-on-surface-variant">
          {/* Первая строка — переключатель сайта; своя настройка страницы — второй строкой. */}
          <span>На сайте: {breadcrumbsWord(siteOn)}</span>
          {canContent ? (
            <Button variant="link" className="min-h-12 px-0 lg:min-h-11" onClick={() => void b.select({ panel: "menu", sub: "crumbs" })}>
              (Весь сайт → Меню → Крошки ›)
            </Button>
          ) : (
            <span>(Весь сайт → Меню → Крошки)</span>
          )}
        </p>
      )}
      {!home && own ? <p className="type-caption text-on-surface">У этой страницы: {own}</p> : null}
      <p className="type-caption text-on-surface-variant">{BREADCRUMBS_MARKUP_NOTE}</p>
    </div>
  );
}
